import {
  IconAlertTriangle,
  IconDownload,
  IconFileSpreadsheet,
  IconFileText,
  IconFileTypePdf,
  IconMusic,
  IconPhoto,
  IconTable,
  IconVideo,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

export interface AttachmentPreviewItem {
  key: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  file?: File;
  url?: string;
}

type PreviewKind = "image" | "pdf" | "spreadsheet" | "table" | "text" | "audio" | "video" | "unsupported";
type PreviewStatus = "idle" | "loading" | "ready" | "error";
type SpreadsheetCell = string | number | boolean | Date | null;

interface PreviewSheet {
  name: string;
  rows: SpreadsheetCell[][];
  totalRows: number;
  totalColumns: number;
  truncated: boolean;
}

const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024;
const MAX_SPREADSHEET_PREVIEW_BYTES = 12 * 1024 * 1024;
const MAX_SPREADSHEET_ROWS = 250;
const MAX_SPREADSHEET_COLUMNS = 50;
const imageZoomLevels = [50, 75, 100, 125, 150, 200] as const;

function fileExtension(filename: string): string {
  const cleanName = filename.split(/[?#]/, 1)[0] || filename;
  const dot = cleanName.lastIndexOf(".");
  return dot >= 0 ? cleanName.slice(dot + 1).toLocaleLowerCase("en-US") : "";
}

function previewKindFor(item: AttachmentPreviewItem): PreviewKind {
  const type = item.contentType.toLocaleLowerCase("en-US").split(";", 1)[0].trim();
  const extension = fileExtension(item.filename);
  if (type.startsWith("image/") || ["avif", "bmp", "gif", "ico", "jpeg", "jpg", "png", "svg", "webp"].includes(extension)) return "image";
  if (type === "application/pdf" || extension === "pdf") return "pdf";
  if (["xlsx", "xlsm", "xltx", "xltm"].includes(extension) || type.includes("spreadsheetml")) return "spreadsheet";
  if (["csv", "tsv"].includes(extension) || ["text/csv", "text/tab-separated-values"].includes(type)) return "table";
  if (type.startsWith("audio/") || ["aac", "flac", "m4a", "mp3", "oga", "ogg", "wav"].includes(extension)) return "audio";
  if (type.startsWith("video/") || ["m4v", "mov", "mp4", "ogv", "webm"].includes(extension)) return "video";
  if (
    type.startsWith("text/")
    || type === "application/json"
    || type.endsWith("+json")
    || type.endsWith("+xml")
    || ["css", "eml", "html", "ics", "ini", "js", "json", "log", "md", "sql", "text", "txt", "vcf", "xml", "yaml", "yml"].includes(extension)
  ) return "text";
  return "unsupported";
}

function previewKindLabel(kind: PreviewKind, filename: string): string {
  if (kind === "image") return "Imagem";
  if (kind === "pdf") return "Documento PDF";
  if (kind === "spreadsheet" || kind === "table") return "Planilha";
  if (kind === "text") return "Documento de texto";
  if (kind === "audio") return "Áudio";
  if (kind === "video") return "Vídeo";
  const extension = fileExtension(filename);
  return extension ? `Arquivo ${extension.toLocaleUpperCase("pt-BR")}` : "Arquivo";
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

function formatCell(value: SpreadsheetCell): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return value.toLocaleString("pt-BR", {
      dateStyle: "short",
      ...(value.getHours() || value.getMinutes() || value.getSeconds() ? { timeStyle: "short" as const } : {}),
    });
  }
  if (typeof value === "boolean") return value ? "VERDADEIRO" : "FALSO";
  if (typeof value === "number") return value.toLocaleString("pt-BR", { maximumFractionDigits: 12 });
  return value;
}

function spreadsheetColumnName(index: number): string {
  let current = index + 1;
  let label = "";
  while (current > 0) {
    current -= 1;
    label = String.fromCharCode(65 + (current % 26)) + label;
    current = Math.floor(current / 26);
  }
  return label;
}

function toPreviewSheet(name: string, rows: SpreadsheetCell[][]): PreviewSheet {
  const totalRows = rows.length;
  const totalColumns = rows.reduce((largest, row) => Math.max(largest, row.length), 0);
  return {
    name,
    rows: rows.slice(0, MAX_SPREADSHEET_ROWS).map((row) => row.slice(0, MAX_SPREADSHEET_COLUMNS)),
    totalRows,
    totalColumns,
    truncated: totalRows > MAX_SPREADSHEET_ROWS || totalColumns > MAX_SPREADSHEET_COLUMNS,
  };
}

function delimiterFor(text: string, extension: string): string {
  if (extension === "tsv") return "\t";
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const candidates = [";", ",", "\t"];
  return candidates.sort((left, right) => firstLine.split(right).length - firstLine.split(left).length)[0];
}

function parseDelimitedText(text: string, delimiter: string): SpreadsheetCell[][] {
  const rows: SpreadsheetCell[][] = [];
  let row: SpreadsheetCell[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (!quoted && character === delimiter) {
      row.push(cell);
      cell = "";
      continue;
    }
    if (!quoted && (character === "\n" || character === "\r")) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => String(value).length > 0)) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += character;
  }
  row.push(cell);
  if (row.some((value) => String(value).length > 0)) rows.push(row);
  return rows;
}

function PreviewTypeIcon({ kind }: { kind: PreviewKind }) {
  if (kind === "image") return <IconPhoto size={21} aria-hidden="true" />;
  if (kind === "pdf") return <IconFileTypePdf size={21} aria-hidden="true" />;
  if (kind === "spreadsheet") return <IconFileSpreadsheet size={21} aria-hidden="true" />;
  if (kind === "table") return <IconTable size={21} aria-hidden="true" />;
  if (kind === "audio") return <IconMusic size={21} aria-hidden="true" />;
  if (kind === "video") return <IconVideo size={21} aria-hidden="true" />;
  return <IconFileText size={21} aria-hidden="true" />;
}

function SpreadsheetPreview({ sheet }: { sheet: PreviewSheet }) {
  if (sheet.rows.length === 0) {
    return <div className="attachment-viewer__empty"><IconTable size={28} aria-hidden="true" /><strong>Planilha vazia</strong><span>Não encontramos células preenchidas nesta aba.</span></div>;
  }
  const columnCount = Math.max(1, Math.min(sheet.totalColumns, MAX_SPREADSHEET_COLUMNS));
  return (
    <div className="attachment-viewer__table-scroll">
      <table>
        <thead>
          <tr><th aria-label="Número da linha" />{Array.from({ length: columnCount }, (_, index) => <th key={index}>{spreadsheetColumnName(index)}</th>)}</tr>
        </thead>
        <tbody>
          {sheet.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <th>{rowIndex + 1}</th>
              {Array.from({ length: columnCount }, (_, columnIndex) => <td key={columnIndex} title={formatCell(row[columnIndex] ?? null)}>{formatCell(row[columnIndex] ?? null)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AttachmentPreviewDialog({ item, onClose }: { item: AttachmentPreviewItem | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [status, setStatus] = useState<PreviewStatus>("idle");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState("");
  const [sheets, setSheets] = useState<PreviewSheet[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [reloadKey, setReloadKey] = useState(0);
  const kind = useMemo(() => item ? previewKindFor(item) : "unsupported", [item?.contentType, item?.filename]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (item && !dialog.open) dialog.showModal();
    if (!item && dialog.open) dialog.close();
  }, [item]);

  useEffect(() => {
    if (!item) {
      setStatus("idle");
      setObjectUrl(null);
      setTextContent("");
      setSheets([]);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let generatedUrl: string | null = null;
    let active = true;
    setStatus("loading");
    setObjectUrl(null);
    setTextContent("");
    setSheets([]);
    setActiveSheet(0);
    setZoomIndex(2);
    setError(null);

    const load = async () => {
      if (kind === "unsupported" && !item.file) {
        setStatus("ready");
        return;
      }
      let blob: Blob | undefined = item.file;
      if (!blob) {
        if (!item.url) throw new Error("O endereço deste arquivo não está disponível.");
        const response = await fetch(item.url, { credentials: "same-origin", signal: controller.signal });
        if (response.status === 401) window.dispatchEvent(new Event("powermail:session-expired"));
        if (!response.ok) throw new Error("Não foi possível carregar este arquivo.");
        blob = await response.blob();
      }
      if (!active) return;
      if (blob.size === 0) throw new Error("Este arquivo está vazio.");

      generatedUrl = URL.createObjectURL(blob);
      setObjectUrl(generatedUrl);

      if (kind === "spreadsheet") {
        if (blob.size > MAX_SPREADSHEET_PREVIEW_BYTES) throw new Error("Esta planilha é grande demais para a visualização rápida. Baixe o arquivo para abri-lo completo.");
        const { default: readExcelFile } = await import("read-excel-file/browser");
        const workbook = await readExcelFile(blob);
        if (!active) return;
        setSheets(workbook.map(({ sheet, data }) => toPreviewSheet(sheet, data as SpreadsheetCell[][])));
      } else if (kind === "table") {
        if (blob.size > MAX_TEXT_PREVIEW_BYTES) throw new Error("Este arquivo tabular é grande demais para a visualização rápida. Baixe o arquivo para abri-lo completo.");
        const text = await blob.text();
        if (!active) return;
        const rows = parseDelimitedText(text, delimiterFor(text, fileExtension(item.filename)));
        setSheets([toPreviewSheet(item.filename, rows)]);
      } else if (kind === "text") {
        const textBlob = blob.size > MAX_TEXT_PREVIEW_BYTES ? blob.slice(0, MAX_TEXT_PREVIEW_BYTES) : blob;
        let text = await textBlob.text();
        if (fileExtension(item.filename) === "json") {
          try { text = JSON.stringify(JSON.parse(text), null, 2); } catch { /* Mantém o texto original quando o JSON estiver incompleto. */ }
        }
        if (blob.size > MAX_TEXT_PREVIEW_BYTES) text += "\n\n— Visualização limitada aos primeiros 2 MB —";
        if (!active) return;
        setTextContent(text);
      }
      if (active) setStatus("ready");
    };

    void load().catch((caught) => {
      if (!active || controller.signal.aborted) return;
      setError(caught instanceof Error ? caught.message : "Não foi possível visualizar este arquivo.");
      setStatus("error");
    });

    return () => {
      active = false;
      controller.abort();
      if (generatedUrl) URL.revokeObjectURL(generatedUrl);
    };
  }, [item?.key, kind, reloadKey]);

  if (!item) return <dialog className="attachment-viewer" ref={dialogRef} />;
  const currentSheet = sheets[Math.min(activeSheet, Math.max(0, sheets.length - 1))] || null;
  const downloadUrl = item.url || objectUrl || undefined;
  const unsupportedExtension = fileExtension(item.filename);

  return (
    <dialog
      className="attachment-viewer"
      ref={dialogRef}
      aria-labelledby="attachment-viewer-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={onClose}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="attachment-viewer__panel">
        <header className="attachment-viewer__header">
          <span className={`attachment-viewer__file-icon is-${kind}`}><PreviewTypeIcon kind={kind} /></span>
          <div className="attachment-viewer__title">
            <span>{previewKindLabel(kind, item.filename)}</span>
            <strong id="attachment-viewer-title" title={item.filename}>{item.filename}</strong>
            <small>{formatFileSize(item.sizeBytes)}{item.contentType ? ` · ${item.contentType}` : ""}</small>
          </div>
          <div className="attachment-viewer__actions">
            {kind === "image" && status === "ready" ? <div className="attachment-viewer__zoom" aria-label="Controle de zoom"><button type="button" onClick={() => setZoomIndex((current) => Math.max(0, current - 1))} disabled={zoomIndex === 0} aria-label="Diminuir zoom"><IconZoomOut size={17} aria-hidden="true" /></button><span>{imageZoomLevels[zoomIndex]}%</span><button type="button" onClick={() => setZoomIndex((current) => Math.min(imageZoomLevels.length - 1, current + 1))} disabled={zoomIndex === imageZoomLevels.length - 1} aria-label="Aumentar zoom"><IconZoomIn size={17} aria-hidden="true" /></button></div> : null}
            {downloadUrl ? <a className="attachment-viewer__download" href={downloadUrl} download={item.file ? item.filename : undefined}><IconDownload size={17} aria-hidden="true" /><span>Baixar</span></a> : null}
            <button className="attachment-viewer__close" type="button" onClick={onClose} aria-label="Fechar visualizador"><IconX size={19} aria-hidden="true" /></button>
          </div>
        </header>

        {sheets.length > 1 ? <nav className="attachment-viewer__sheet-tabs" aria-label="Abas da planilha">{sheets.map((sheet, index) => <button className={index === activeSheet ? "is-active" : ""} type="button" onClick={() => setActiveSheet(index)} key={`${sheet.name}-${index}`}>{sheet.name}</button>)}</nav> : null}

        <main className={`attachment-viewer__stage is-${kind}`} aria-busy={status === "loading"}>
          {status === "loading" ? <div className="attachment-viewer__loading" role="status"><i /><i /><i /><span>Preparando visualização…</span></div> : null}
          {status === "error" ? <div className="attachment-viewer__empty is-error" role="alert"><IconAlertTriangle size={30} aria-hidden="true" /><strong>Não conseguimos mostrar a prévia</strong><span>{error}</span><button type="button" onClick={() => setReloadKey((current) => current + 1)}>Tentar novamente</button></div> : null}
          {status === "ready" && kind === "image" && objectUrl ? <div className={`attachment-viewer__image is-zoom-${imageZoomLevels[zoomIndex]}`}><img src={objectUrl} alt={`Pré-visualização de ${item.filename}`} /></div> : null}
          {status === "ready" && kind === "pdf" && objectUrl ? <iframe className="attachment-viewer__pdf" src={objectUrl} title={`Pré-visualização de ${item.filename}`} /> : null}
          {status === "ready" && kind === "video" && objectUrl ? <video className="attachment-viewer__video" src={objectUrl} controls preload="metadata">Seu navegador não consegue reproduzir este vídeo.</video> : null}
          {status === "ready" && kind === "audio" && objectUrl ? <div className="attachment-viewer__audio"><IconMusic size={36} aria-hidden="true" /><strong>{item.filename}</strong><audio src={objectUrl} controls preload="metadata">Seu navegador não consegue reproduzir este áudio.</audio></div> : null}
          {status === "ready" && kind === "text" ? <pre className="attachment-viewer__text">{textContent}</pre> : null}
          {status === "ready" && (kind === "spreadsheet" || kind === "table") && currentSheet ? <><SpreadsheetPreview sheet={currentSheet} />{currentSheet.truncated ? <p className="attachment-viewer__limit">Mostrando até {MAX_SPREADSHEET_ROWS} linhas e {MAX_SPREADSHEET_COLUMNS} colunas de {currentSheet.totalRows.toLocaleString("pt-BR")} × {currentSheet.totalColumns.toLocaleString("pt-BR")}.</p> : <p className="attachment-viewer__limit">{currentSheet.totalRows.toLocaleString("pt-BR")} linhas · {currentSheet.totalColumns.toLocaleString("pt-BR")} colunas</p>}</> : null}
          {status === "ready" && kind === "unsupported" ? <div className="attachment-viewer__empty"><IconFileText size={32} aria-hidden="true" /><strong>Prévia indisponível para este formato</strong><span>{unsupportedExtension === "xls" ? "Planilhas .xls antigas precisam ser baixadas e abertas no Excel. Arquivos .xlsx podem ser vistos diretamente aqui." : "Você pode baixar o arquivo com segurança para abri-lo no aplicativo correspondente."}</span></div> : null}
        </main>
      </div>
    </dialog>
  );
}
